import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiMove, FiLayers, FiClock } from 'react-icons/fi';
import { cleaningTypesAPI, bedDispositionsAPI, roomGroupsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import ConfirmDialog from '../common/ConfirmDialog';
import BedDispositionDialog from './BedDispositionDialog';
import CleaningTypeDialog from './CleaningTypeDialog';
import RoomGroupDialog from './RoomGroupDialog';
import { getModifierDescription } from '../../utils/timeCalculator';

const AdditionalSettingsTab = () => {
  const { user } = useAuth();
  const { hasFeature } = useApp();

  // Cleaning Types State
  const [cleaningTypes, setCleaningTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [typeDialog, setTypeDialog] = useState(null); // null, 'new', or type object
  const [draggedType, setDraggedType] = useState(null);
  const [dragOverTypeIndex, setDragOverTypeIndex] = useState(null);
  const [deleteTypeConfirm, setDeleteTypeConfirm] = useState(null);

  // Bed Dispositions State
  const [bedDispositions, setBedDispositions] = useState([]);
  const [loadingDispositions, setLoadingDispositions] = useState(true);
  const [dispositionDialog, setDispositionDialog] = useState(null); // null, 'new', or disposition object
  const [draggedDisposition, setDraggedDisposition] = useState(null);
  const [dragOverDispositionIndex, setDragOverDispositionIndex] = useState(null);
  const [deleteDispositionConfirm, setDeleteDispositionConfirm] = useState(null);

  // Room Groups State
  const [roomGroups, setRoomGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groupDialog, setGroupDialog] = useState(null); // null, 'new', or group object
  const [draggedGroup, setDraggedGroup] = useState(null);
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);

  useEffect(() => {
    fetchCleaningTypes();
    fetchBedDispositions();
    fetchRoomGroups();
  }, []);

  // CLEANING TYPES FUNCTIONS
  const fetchCleaningTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await cleaningTypesAPI.getAll();
      setCleaningTypes(response);
    } catch (error) {
      console.error('Error fetching cleaning types:', error);
      setCleaningTypes([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleSaveType = async (data) => {
    try {
      if (typeDialog === 'new') {
        // Creating new type
        const dataToSend = {
          ...data,
          establishment_id: user.establishment_id
        };
        const newType = await cleaningTypesAPI.create(dataToSend);
        setCleaningTypes(prev => [...prev, newType]);
      } else {
        // Editing existing type
        const updatedType = await cleaningTypesAPI.update(typeDialog.id, data);
        setCleaningTypes(prev =>
          prev.map(type =>
            type.id === typeDialog.id ? updatedType : type
          )
        );
      }
      setTypeDialog(null);
    } catch (error) {
      console.error('Error saving cleaning type:', error);
      alert('Error al guardar el tipo de limpieza. Por favor, inténtalo de nuevo.');
      throw error;
    }
  };

  const handleDeleteType = (type) => {
    setDeleteTypeConfirm(type);
  };

  const confirmDeleteType = async () => {
    try {
      await cleaningTypesAPI.delete(deleteTypeConfirm.id);
      setCleaningTypes(prev => prev.filter(type => type.id !== deleteTypeConfirm.id));
      setDeleteTypeConfirm(null);
    } catch (error) {
      console.error('Error deleting cleaning type:', error);
      alert('Error al eliminar el tipo de limpieza. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDragStartType = (e, index) => {
    setDraggedType(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverType = (e, index) => {
    e.preventDefault();
    setDragOverTypeIndex(index);
  };

  const handleDropType = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedType === null || draggedType === dropIndex) {
      setDraggedType(null);
      setDragOverTypeIndex(null);
      return;
    }

    const newItems = [...cleaningTypes];
    const [movedItem] = newItems.splice(draggedType, 1);
    newItems.splice(dropIndex, 0, movedItem);
    setCleaningTypes(newItems);

    const reorderData = newItems.map((item, index) => ({
      id: item.id,
      display_order: index
    }));

    try {
      await cleaningTypesAPI.reorder(reorderData);
    } catch (error) {
      console.error('Error reordering cleaning types:', error);
      fetchCleaningTypes();
      alert('Error al reordenar los tipos de limpieza. Por favor, inténtalo de nuevo.');
    }

    setDraggedType(null);
    setDragOverTypeIndex(null);
  };

  // BED DISPOSITIONS FUNCTIONS
  const fetchBedDispositions = async () => {
    try {
      setLoadingDispositions(true);
      const response = await bedDispositionsAPI.getAll();
      setBedDispositions(response);
    } catch (error) {
      console.error('Error fetching bed dispositions:', error);
      setBedDispositions([]);
    } finally {
      setLoadingDispositions(false);
    }
  };

  const handleSaveDisposition = async (data) => {
    try {
      if (dispositionDialog === 'new') {
        // Creating new disposition
        const dataToSend = {
          ...data,
          establishment_id: user.establishment_id
        };
        const newBedDisposition = await bedDispositionsAPI.create(dataToSend);
        setBedDispositions(prev => [...prev, newBedDisposition]);
      } else {
        // Editing existing disposition
        const updatedDisposition = await bedDispositionsAPI.update(dispositionDialog.id, data);
        setBedDispositions(prev =>
          prev.map(disposition =>
            disposition.id === dispositionDialog.id ? updatedDisposition : disposition
          )
        );
      }
      setDispositionDialog(null);
    } catch (error) {
      console.error('Error saving bed disposition:', error);
      alert('Error al guardar la disposición de camas. Por favor, inténtalo de nuevo.');
      throw error;
    }
  };

  const handleDeleteDisposition = (disposition) => {
    setDeleteDispositionConfirm(disposition);
  };

  const confirmDeleteDisposition = async () => {
    try {
      await bedDispositionsAPI.delete(deleteDispositionConfirm.id);
      setBedDispositions(prev => prev.filter(disposition => disposition.id !== deleteDispositionConfirm.id));
      setDeleteDispositionConfirm(null);
    } catch (error) {
      console.error('Error deleting bed disposition:', error);
      alert('Error al eliminar la disposición de camas. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDragStartDisposition = (e, index) => {
    setDraggedDisposition(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverDisposition = (e, index) => {
    e.preventDefault();
    setDragOverDispositionIndex(index);
  };

  const handleDropDisposition = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedDisposition === null || draggedDisposition === dropIndex) {
      setDraggedDisposition(null);
      setDragOverDispositionIndex(null);
      return;
    }

    const newItems = [...bedDispositions];
    const [movedItem] = newItems.splice(draggedDisposition, 1);
    newItems.splice(dropIndex, 0, movedItem);
    setBedDispositions(newItems);

    const reorderData = newItems.map((item, index) => ({
      id: item.id,
      display_order: index
    }));

    try {
      await bedDispositionsAPI.reorder(reorderData);
    } catch (error) {
      console.error('Error reordering bed dispositions:', error);
      fetchBedDispositions();
      alert('Error al reordenar las disposiciones de camas. Por favor, inténtalo de nuevo.');
    }

    setDraggedDisposition(null);
    setDragOverDispositionIndex(null);
  };

  // ROOM GROUPS FUNCTIONS
  const fetchRoomGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await roomGroupsAPI.getAll();
      setRoomGroups(response);
    } catch (error) {
      console.error('Error fetching room groups:', error);
      setRoomGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSaveGroup = async (data) => {
    try {
      if (groupDialog === 'new') {
        // Creating new group
        const dataToSend = {
          ...data,
          establishment_id: user.establishment_id
        };
        const newGroup = await roomGroupsAPI.create(dataToSend);
        setRoomGroups(prev => [...prev, newGroup]);
      } else {
        // Editing existing group
        const updatedGroup = await roomGroupsAPI.update(groupDialog.id, data);
        setRoomGroups(prev =>
          prev.map(group =>
            group.id === groupDialog.id ? updatedGroup : group
          )
        );
      }
      setGroupDialog(null);
    } catch (error) {
      console.error('Error saving room group:', error);
      alert('Error al guardar el grupo de habitaciones. Por favor, inténtalo de nuevo.');
      throw error;
    }
  };

  const handleDeleteGroup = (group) => {
    setDeleteGroupConfirm(group);
  };

  const confirmDeleteGroup = async () => {
    try {
      await roomGroupsAPI.delete(deleteGroupConfirm.id);
      setRoomGroups(prev => prev.filter(group => group.id !== deleteGroupConfirm.id));
      setDeleteGroupConfirm(null);
    } catch (error) {
      console.error('Error deleting room group:', error);
      alert('Error al eliminar el grupo de habitaciones. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDragStartGroup = (e, index) => {
    setDraggedGroup(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverGroup = (e, index) => {
    e.preventDefault();
    setDragOverGroupIndex(index);
  };

  const handleDropGroup = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedGroup === null || draggedGroup === dropIndex) {
      setDraggedGroup(null);
      setDragOverGroupIndex(null);
      return;
    }

    const newItems = [...roomGroups];
    const [movedItem] = newItems.splice(draggedGroup, 1);
    newItems.splice(dropIndex, 0, movedItem);
    setRoomGroups(newItems);

    // Update display_order for each item
    try {
      for (let i = 0; i < newItems.length; i++) {
        if (newItems[i].display_order !== i) {
          await roomGroupsAPI.updateOrder(newItems[i].id, i);
        }
      }
    } catch (error) {
      console.error('Error reordering room groups:', error);
      fetchRoomGroups();
      alert('Error al reordenar los grupos. Por favor, inténtalo de nuevo.');
    }

    setDraggedGroup(null);
    setDragOverGroupIndex(null);
  };

  if (loadingTypes || loadingDispositions || loadingGroups) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* TIPOS DE LIMPIEZA */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Tipos de Limpieza
            </h2>
            {hasFeature('custom_cleaning_types') && (
              <button
                onClick={() => setTypeDialog('new')}
                className="text-blue-600 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                title="Nuevo tipo"
              >
                <FiPlus className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* List of cleaning types */}
          <div className="space-y-2">
            {cleaningTypes.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No hay tipos de limpieza configurados
              </div>
            ) : (
              cleaningTypes.map((type, index) => (
                <div
                  key={type.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStartType(e, index)}
                  onDragOver={(e) => handleDragOverType(e, index)}
                  onDragLeave={() => setDragOverTypeIndex(null)}
                  onDrop={(e) => handleDropType(e, index)}
                  onDragEnd={() => {
                    setDraggedType(null);
                    setDragOverTypeIndex(null);
                  }}
                  className={`animate-fade-in flex items-center justify-between p-3 bg-gray-50 rounded-lg border transition-all cursor-move hover:bg-gray-100 hover:shadow-sm ${
                    dragOverTypeIndex === index ? 'border-blue-500 border-2 bg-blue-50' : ''
                  } ${draggedType === index ? 'opacity-50' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start space-x-2 flex-1 min-w-0">
                    <FiMove className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900 block">
                        {type.name}
                      </span>
                      {(type.includes_sheet_change || type.includes_towel_change || (type.time_modifier_type && type.time_modifier_type !== 'none')) && (
                        <div className="text-xs text-gray-600 mt-0.5 space-y-0.5">
                          {type.includes_sheet_change && <div>✓ Cambio de sábanas</div>}
                          {type.includes_towel_change && <div>✓ Cambio de toallas</div>}
                          {type.time_modifier_type && type.time_modifier_type !== 'none' && (
                            <div className="flex items-center gap-1 text-blue-600">
                              <FiClock className="h-3 w-3" />
                              <span>{getModifierDescription(type.time_modifier_type, type.time_modifier_value)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setTypeDialog(type)}
                      className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-md hover:bg-indigo-50 transition-colors"
                      title="Editar"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    {hasFeature('custom_cleaning_types') && (
                      <button
                        onClick={() => handleDeleteType(type)}
                        className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        title="Eliminar"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* DISPOSICIONES DE CAMAS */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Disposiciones de Camas
            </h2>
            {hasFeature('custom_bed_dispositions') && (
              <button
                onClick={() => setDispositionDialog('new')}
                className="text-blue-600 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                title="Nueva disposición"
              >
                <FiPlus className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* List of bed dispositions */}
          <div className="space-y-2">
            {bedDispositions.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No hay disposiciones de camas configuradas
              </div>
            ) : (
              bedDispositions.map((disposition, index) => (
                <div
                  key={disposition.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStartDisposition(e, index)}
                  onDragOver={(e) => handleDragOverDisposition(e, index)}
                  onDragLeave={() => setDragOverDispositionIndex(null)}
                  onDrop={(e) => handleDropDisposition(e, index)}
                  onDragEnd={() => {
                    setDraggedDisposition(null);
                    setDragOverDispositionIndex(null);
                  }}
                  className={`animate-fade-in flex items-center justify-between p-3 bg-gray-50 rounded-lg border transition-all cursor-move hover:bg-gray-100 hover:shadow-sm ${
                    dragOverDispositionIndex === index ? 'border-blue-500 border-2 bg-blue-50' : ''
                  } ${draggedDisposition === index ? 'opacity-50' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start space-x-2 flex-1 min-w-0">
                    <FiMove className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900 block">
                        {disposition.name}
                      </span>
                      <div className="text-xs text-gray-600">
                        {disposition.min_beds === disposition.max_beds ? (
                          <>Exactamente {disposition.min_beds} cama{disposition.min_beds > 1 ? 's' : ''}</>
                        ) : disposition.max_beds ? (
                          <>De {disposition.min_beds} a {disposition.max_beds} camas</>
                        ) : (
                          <>{disposition.min_beds} o más camas</>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Sábanas: {parseFloat(disposition.sheet_cost || 0).toFixed(2)}€ | Toallas: {parseFloat(disposition.towel_cost || 0).toFixed(2)}€
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setDispositionDialog(disposition)}
                      className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-md hover:bg-indigo-50 transition-colors"
                      title="Editar"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    {hasFeature('custom_bed_dispositions') && (
                      <button
                        onClick={() => handleDeleteDisposition(disposition)}
                        className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        title="Eliminar"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* GRUPOS DE HABITACIONES */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Grupos de Habitaciones
            </h2>
            <button
              onClick={() => setGroupDialog('new')}
              className="text-blue-600 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
              title="Nuevo grupo"
            >
              <FiPlus className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4">
          <p className="text-xs text-gray-500 mb-3">
            Los grupos organizan las habitaciones en la tabla de órdenes de limpieza.
          </p>
          {/* List of room groups */}
          <div className="space-y-2">
            {roomGroups.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No hay grupos de habitaciones configurados
              </div>
            ) : (
              roomGroups.map((group, index) => (
                <div
                  key={group.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStartGroup(e, index)}
                  onDragOver={(e) => handleDragOverGroup(e, index)}
                  onDragLeave={() => setDragOverGroupIndex(null)}
                  onDrop={(e) => handleDropGroup(e, index)}
                  onDragEnd={() => {
                    setDraggedGroup(null);
                    setDragOverGroupIndex(null);
                  }}
                  className={`animate-fade-in flex items-center justify-between p-3 bg-gray-50 rounded-lg border transition-all cursor-move hover:bg-gray-100 hover:shadow-sm ${
                    dragOverGroupIndex === index ? 'border-blue-500 border-2 bg-blue-50' : ''
                  } ${draggedGroup === index ? 'opacity-50' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start space-x-2 flex-1 min-w-0">
                    <FiMove className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex items-center space-x-2">
                      <FiLayers className="h-4 w-4 text-purple-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 block">
                        {group.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => setGroupDialog(group)}
                      className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-md hover:bg-indigo-50 transition-colors"
                      title="Editar"
                    >
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group)}
                      className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialogs */}
      {deleteTypeConfirm && (
        <ConfirmDialog
          title="Eliminar Tipo de Limpieza"
          message={`¿Estás seguro de que deseas eliminar el tipo de limpieza "${deleteTypeConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteType}
          onCancel={() => setDeleteTypeConfirm(null)}
        />
      )}

      {deleteDispositionConfirm && (
        <ConfirmDialog
          title="Eliminar Disposición de Camas"
          message={`¿Estás seguro de que deseas eliminar la disposición "${deleteDispositionConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteDisposition}
          onCancel={() => setDeleteDispositionConfirm(null)}
        />
      )}

      {/* Cleaning Type Dialog */}
      {typeDialog && (
        <CleaningTypeDialog
          cleaningType={typeDialog === 'new' ? null : typeDialog}
          onSave={handleSaveType}
          onClose={() => setTypeDialog(null)}
        />
      )}

      {/* Bed Disposition Dialog */}
      {dispositionDialog && (
        <BedDispositionDialog
          disposition={dispositionDialog === 'new' ? null : dispositionDialog}
          onSave={handleSaveDisposition}
          onClose={() => setDispositionDialog(null)}
        />
      )}

      {/* Delete Group Confirmation */}
      {deleteGroupConfirm && (
        <ConfirmDialog
          title="Eliminar Grupo de Habitaciones"
          message={`¿Estás seguro de que deseas eliminar el grupo "${deleteGroupConfirm.name}"? Las habitaciones de este grupo quedarán sin grupo asignado.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteGroup}
          onCancel={() => setDeleteGroupConfirm(null)}
        />
      )}

      {/* Room Group Dialog */}
      {groupDialog && (
        <RoomGroupDialog
          group={groupDialog === 'new' ? null : groupDialog}
          onSave={handleSaveGroup}
          onClose={() => setGroupDialog(null)}
        />
      )}
    </div>
  );
};

export default AdditionalSettingsTab;
